<!--
  HomePage.vue -- P5 storefront (Home page).

  Card: Live Storefront - Home/Category/Product (ADR 0060/0061)
  https://trello.com/c/q7EQ3a0b
  Pebble: P5 -- Home + Category pages + route tree (frontend).

  The canonical storefront landing at /shop/<storeSlug>. Rendered inside
  StorefrontLayout (brand banner topbar) via meta.layout='storefront'. Layout:

    StorefrontHero        (campaign carousel slides)
    home category banner  (if set)
    home welcome message  (sanitized v-html sink, if set)
    StorefrontProductGrid (home category products, in sort_order)
    StorefrontFooter

  Data:
    - useStorefrontChrome(slug): brand color + store name (committed to Vuex
      so the topbar themes) + render-ready hero slides + sanitized message.
    - useStorefrontCategory(slug, ""): the HOME category (empty slug) -- its
      banner_image + rich_text + ordered products.

  Preview-safe: this page NEVER swaps the shopper token. The StorefrontSessionGate
  parent is the single establish-point; ?preview=1 skips the swap there, and
  this page only performs plain reads. banner/message blocks collapse when
  absent (no empty frames). An empty home category (no products, no banner, no
  text) is a valid sparse page, not an error.
-->
<template>
  <div class="sf-home" data-testid="sf-home-page">

    <div
      v-if="chromeLoading || categoryLoading"
      class="sf-home__loading"
      data-testid="sf-home-loading"
    >
      Loading storefront. Please wait...
    </div>

    <div
      v-else-if="fatalError"
      class="sf-home__error"
      data-testid="sf-home-error"
    >
      {{ fatalError.message }}
    </div>

    <template v-else>
      <!-- Full-bleed band: the hero spans edge-to-edge (matches the comp);
           StorefrontHero width-caps its own inner split grid.

           store-name + brand-color drive the split band's copy column. Both
           are read from the SAME Vuex getters the topbar reads (committed by
           useStorefrontChrome), so the hero headline and the topbar can never
           name the store differently (design principle 4). -->
      <storefront-hero
        v-if="heroSlides.length > 0"
        :slides="heroSlides"
        :interval-ms="heroIntervalMs"
        :store-name="storeName"
        :brand-color="brandColor"
      />

      <!-- Everything below the hero reads as composed sections (vertical
           rhythm, item 3) inside the shared width-capped container (item 1)
           instead of a full-bleed stack of divs. -->
      <div class="sf-home__body sf-container">
        <img
          v-if="bannerUrl"
          :src="bannerUrl"
          class="sf-home__banner sf-home__section"
          data-testid="sf-home-banner"
          :alt="homeName"
        />

        <!-- SECURITY (stored-XSS, CWE-79): rich_text / message are
             distributor-authored. Both are re-sanitized at THIS render sink
             (sanitizeMessageHtml) even though the backend sanitized at write
             time -- the persisted columns are craftable via the API, so the
             sink sanitizes independently. Never a raw v-html. The tinted
             card wrapper below is presentation only -- it does not touch
             the sanitize call or the v-html sink. rich_text still wins over
             message when both are present (unchanged precedence). -->
        <div
          v-if="richText || sanitizedMessage"
          class="sf-home__welcome sf-home__section"
          data-testid="sf-home-welcome"
          :style="welcomeAccentStyle"
        >
          <div
            v-if="richText"
            class="sf-home__richtext shopper-message-body"
            data-testid="sf-home-richtext"
            v-html="richText"
          />
          <div
            v-else
            class="sf-home__message shopper-message-body"
            data-testid="sf-home-message"
            v-html="sanitizedMessage"
          />
        </div>

        <storefront-product-grid
          v-if="products.length > 0"
          class="sf-home__section"
          :products="products"
          :store-slug="slug"
          :category-slug="''"
        />
        <div
          v-else
          class="sf-home__empty sf-home__section"
          data-testid="sf-home-empty"
        >
          No products available yet.
        </div>
      </div>
    </template>

    <storefront-footer
      :policies="policies"
      :store-slug="slug"
      :white-label="whiteLabel"
    />
  </div>
</template>

<script>
import { resolveStorefrontSlug } from "@/router/storefrontSlug";
import { sanitizeMessageHtml } from "@/utils/sanitizeHtml";
import { buildImageUrl } from "@/utils/imageBaseUrl";
import { storefrontBrandAccentVars } from "@/utils/storefrontBrandAccent";
import { useStorefrontChrome } from "@/composables/useStorefrontChrome";
import { useStorefrontCategory } from "@/composables/useStorefrontCategory";

import StorefrontHero from "@/components/Storefront/StorefrontHero.vue";
import StorefrontProductGrid from "@/components/Storefront/StorefrontProductGrid.vue";
import StorefrontFooter from "@/components/Storefront/StorefrontFooter.vue";

export default {
  name: "StorefrontHomePage",

  components: {
    "storefront-hero": StorefrontHero,
    "storefront-product-grid": StorefrontProductGrid,
    "storefront-footer": StorefrontFooter,
  },

  setup() {
    const {
      heroSlides,
      heroIntervalMs,
      sanitizedMessage,
      // Tax & Legal policy URLs -> the footer's optional legal links.
      policies,
      // White-label branding -> the footer's brand mark
      // (card https://trello.com/c/mq26K91V).
      whiteLabel,
      loading: chromeLoading,
      error: chromeError,
      fetchChrome,
    } = useStorefrontChrome();

    const {
      category,
      loading: categoryLoading,
      error: categoryError,
      fetchCategory,
    } = useStorefrontCategory();

    return {
      heroSlides,
      heroIntervalMs,
      sanitizedMessage,
      policies,
      whiteLabel,
      chromeLoading,
      chromeError,
      fetchChrome,
      category,
      categoryLoading,
      categoryError,
      fetchCategory,
    };
  },

  computed: {
    slug() {
      return resolveStorefrontSlug(this.$route) || "";
    },

    // A fatal error is one that leaves the page with NOTHING to render. A
    // category 404 is fatal (no store / no home category); chrome failure
    // alone still lets the category render, so it is NOT fatal on its own.
    fatalError() {
      return this.categoryError || null;
    },

    homeName() {
      return this.category?.name || "";
    },

    bannerUrl() {
      const banner = this.category?.banner_image;
      return banner ? buildImageUrl(banner) : null;
    },

    // Re-sanitize the category rich_text at the render sink (defense-in-depth
    // over the backend write-time sanitize). Empty -> falsy so the block
    // collapses.
    richText() {
      return sanitizeMessageHtml(this.category?.rich_text || "");
    },

    products() {
      return Array.isArray(this.category?.products)
        ? this.category.products
        : [];
    },

    // Item 5 (brand color beyond the topbar): the welcome card's accent bar
    // + heading ink, derived from the SAME brand-color source the topbar
    // themes from (getBrandColor, committed to Vuex by useStorefrontChrome).
    // storefrontBrandAccentVars degrades to {} for a missing/invalid color,
    // which leaves the CSS var(..., fallback) defaults in control -- never
    // a broken/transparent accent.
    welcomeAccentStyle() {
      return storefrontBrandAccentVars(this.brandColor);
    },

    // The store's brand color, from the SAME getter the topbar reads.
    // Shared by the welcome card's accent and the hero band's headline ink,
    // so the two can never derive from different colors.
    brandColor() {
      return this.$store?.getters?.getBrandColor || "";
    },

    // The split-band hero's headline. Reads the RAW getter deliberately:
    // StorefrontLayout's topbar falls back to "Shop" for a missing name,
    // which is a sensible label for a small chrome element but a poor
    // page headline. An unresolved name collapses the hero's copy column
    // instead (principle 5 -- fail toward the record, not a filler word).
    storeName() {
      return this.$store?.getters?.getStoreName || "";
    },
  },

  watch: {
    slug: {
      immediate: true,
      handler(newSlug) {
        if (!newSlug) return;
        this.loadAll(newSlug);
      },
    },
  },

  methods: {
    loadAll(slug) {
      // Home category = empty category slug (backend resolves is_home).
      this.fetchChrome(slug);
      this.fetchCategory(slug, "");
    },
  },
};
</script>

<style scoped>
/* Sticky footer (Jake, 2026-08-26): the legal-links footer sits at the
   BOTTOM OF THE VIEWPORT when the page content is shorter than the
   viewport, and below the content when it is longer.

   Deliberately `min-height: 100%` and NOT `100dvh`. StorefrontLayout is a
   fixed-viewport shell (`.storefront-layout` is `height: 100dvh;
   overflow: hidden`) whose `.storefront-main` is the scroll container
   (`flex: 1; overflow-y: auto`). Declaring a viewport height here would
   stack a SECOND full viewport inside that scroller and force a scrollbar
   on a short page -- the opposite of the ask. Filling the scroll
   container's height is exactly enough.

   `margin-top: auto` on the footer (the flex column's last child) then
   consumes the free space above it. On a long page there is no free space,
   so the footer simply follows the content. No `position: fixed` -- that
   would overlay content on long pages. */
.sf-home {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.sf-home > .sf-footer {
  margin-top: auto;
}

.sf-home__loading,
.sf-home__error,
.sf-home__empty {
  padding: 48px 32px;
  text-align: center;
  color: var(--tf-fg-3, #616161);
}

/* Item 1 + 3: the width-capped container that replaces the old full-bleed
   stack, with its own side padding (the individual sections below no
   longer carry one) and vertical rhythm between sections (item 3 -- Home
   previously had three padding-only rules and no section spacing scale). */
.sf-home__body {
  padding: 0 32px 48px;
}

.sf-home__section + .sf-home__section {
  margin-top: 40px;
}

.sf-home__banner {
  display: block;
  width: 100%;
  max-height: 320px;
  object-fit: cover;
  border-radius: var(--tf-radius-md, 12px);
}

/* Item 4: the welcome message reads as a contained, tinted block with clear
   hierarchy instead of a bare line of text. Presentation only -- the
   sanitize call (sanitizeMessageHtml) and the v-html sink underneath are
   completely untouched; this wrapper just styles the box the sanitized
   markup renders into. */
.sf-home__welcome {
  background: var(--tf-surface-light, #f5f8fa);
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: var(--tf-radius-md, 12px);
  /* Item 5: a thin brand-color accent bar on the welcome card carries the
     store's brand color beyond the topbar. Falls back to --tf-primary so a
     store with no brand_color set (or an unresolved getter) still renders a
     sensible accent rather than a blank/transparent edge. */
  border-left: 4px solid var(--sf-brand-color, var(--tf-primary, #119ae9));
}

.sf-home__richtext,
.sf-home__message {
  padding: 20px 24px;
  color: var(--tf-fg-2, #424242);
  line-height: 1.55;
}

/* First-child heading inside the sanitized rich text picks up the brand
   color as an accent (item 5) -- degrades gracefully for ANY brand color
   because --sf-brand-ink is derived by storefrontBrandAccentVars()
   (WCAG contrast against the card's light surface), not assumed to be dark
   navy. A store with a very light/vivid brand color that would fail AA as
   raw heading ink gets a darkened-toward-the-brand-hue ink instead (see
   utils/storefrontBrandAccent.js); the CSS var(..., fallback) here only
   covers the case where no brand color resolved at all. */
.sf-home__richtext:deep(h1:first-child),
.sf-home__richtext:deep(h2:first-child),
.sf-home__richtext:deep(h3:first-child) {
  color: var(--sf-brand-ink, var(--tf-fg-1, #212121));
  margin-top: 0;
}
</style>
